import { usePersistedDataStore, useLocalDataStore, useCompStore } from '../components/store';
import { useState, useCallback, useRef, useEffect } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import { toast } from './toast';
import axiosInstance from '../fetchConfig/axiosConfig';
import { fieldMappings, excludedFields, specialFormatters, conditionMap, conditionNumericMap, gradeMap, gradeNumericMap } from '../common/mappings';
import { calculateDistance } from './dateUtils';
import { generateErrorFlags, generateErrorMessages, generateErrorCodes } from '../utils/compErrorFlagging';
import { useCompLookups } from './useCompLookups';
import { calculateCompAdjustedPrice } from '../utils/adjustmentCalculator';
import logger from '../utils/logger';
import { parseJsonArrayFields } from './jsonArrayFields';

/**
 * Stream Failure Error Handling
 * 
 * This module implements automatic detection and handling of stream failures
 * where the second element of parsed update arrays is an empty object.
 * 
 * When a StreamFailureError is thrown, the caller should:
 * 1. Detect the error type (err instanceof StreamFailureError)
 * 2. Implement retry logic with exponential backoff
 * 3. Make a fresh request to get a new stream
 * 4. Retry the stream processing from the beginning
 * 
 * Example retry usage:
 * try {
 *   await processStream({ compStreamObjectRef, stream, negotiationObj });
 * } catch (err) {
 *   if (err instanceof StreamFailureError) {
 *     // Implement retry logic here
 *     await retryFromStart();
 *   } else {
 *     // Handle other errors normally
 *     throw err;
 *   }
 * }
 * 

 */

// Custom error so caller can distinguish and retry from the start
export class StreamFailureError extends Error {
  constructor(message = 'Stream returned empty second index (failure detected)') {
    super(message);
    this.name = 'StreamFailureError';
  }
}

/**
 * Utility: Check if a value is a plain object (not null, not array, not primitive)
 * @param {any} v - Value to check
 * @returns {boolean} True if plain object
 */
const isPlainObject = (v) =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

/**
 * Utility: Detect stream failure condition where 2nd index is an empty object
 * This indicates the upstream started sending blank chunks and the stream should fail
 * @param {any} update - Parsed JSON update from stream
 * @returns {boolean} True if 2nd index is empty object (stream failure)
 */
const isSecondIndexEmptyObject = (update) => {
  if (!Array.isArray(update) || update.length < 2) return false;
  const idx1 = update[1];
  return isPlainObject(idx1) && Object.keys(idx1).length === 0;
};

/**
 * Utility: Detect server error responses in stream data
 * This detects JSON objects with error status that indicate server failures
 * @param {any} update - Parsed JSON update from stream
 * @returns {boolean} True if update contains server error
 */
export const isServerErrorResponse = (update) => {
  if (!isPlainObject(update)) return false;
  
  // Check for common error response patterns
  return (
    (update.status === 'error' && update.error) ||
    (update.error && typeof update.error === 'string' && update.error.includes('Internal Server Error')) ||
    (update.other && update.error && update.status === 'error')
  );
};

export const fields = [
  { field: 'id', headerName: '#', width: 80, type: 'string' },
  { field: 'parcel_id', headerName: 'Parcel ID', type: 'string' },
  { field: 'Address', headerName: 'Address', type: 'string' },
  { field: 'Town', headerName: 'Town', type: 'string' },
  { field: 'ZipCode', headerName: 'Zip Code', type: 'string' },
  { field: 'IFMV', headerName: 'IFMV', type: 'numeric' },
  { field: 'Taxes', headerName: 'Taxes', type: 'numeric' },
  { field: 'WorkupValueAvg', headerName: 'Workup Value Avg', type: 'numeric' },
  { field: 'WorkupValueMid', headerName: 'Workup Value Mid', type: 'numeric' },
  { field: 'WorkupValueAvgOptim', headerName: 'Workup Value Avg (Optimized)', type: 'numeric' },
  { field: 'WorkupValueMidOptim', headerName: 'Workup Value Mid (Optimized)', type: 'numeric' },
  // this value is PRE heuristics
  { field: 'KNNWorkupValue', headerName: 'KNN Workup Value', type: 'numeric' },
  // this value is POST heuristics
  { field: 'MarketingValuePrediction', headerName: 'Marketing Value Prediction', type: 'numeric' },
  { field: 'ProjectedPotentialSavings', headerName: 'Projected Potential Savings', type: 'numeric' },
  { field: 'ProjectedAventineRevenue', headerName: 'Projected Aventine Revenue', type: 'numeric' },
  { field: 'TaxReductionPct', headerName: 'Tax Reduction Pct', type: 'percent' },
  { field: 'CaseDescription', headerName: 'Case Description', type: 'string' },
  { field: 'SalePrice', headerName: 'Sale Price', type: 'numeric' },
  { field: 'SalePrice_pred', headerName: 'Sale Price Prediction', type: 'numeric' },
  { field: 'Total_Possible_Neighbors', headerName: 'Total Possible Neighbors', type: 'numeric' },
  { field: 'Total_Regular_sales', headerName: 'Total Regular Sales', type: 'numeric' },
  { field: 'Total_Optimize_Sales', headerName: 'Total Optimize Sales', type: 'numeric' },

  { field: 'average_index_optim', headerName: 'Our Average Index', type: 'numeric' },
  { field: 'average_index_opp', headerName: 'Town Average Index', type: 'numeric' },
  // Percentage of the 
  { field: 'KNNRegOptimWorkupRatio', headerName: 'Reg-Optim Workup Ratio', type: 'percent' },
  { field: 'PropertyAssessment', headerName: 'Property Assessment', type: 'numeric' },
  { field: 'SaleDate', headerName: 'Sale Date', type: 'string' },
  { field: 'Name', headerName: 'Name', type: 'string' },
  { field: 'FirstName', headerName: 'First Name', type: 'string' },
  { field: 'LastName', headerName: 'Last Name', type: 'string' },
  { field: 'SettledMktVal', headerName: 'Settled Market Value', type: 'numeric' },
  { field: 'do_not_contact', headerName: 'Do Not Contact', type: 'numeric' },
  { field: 'prev_reduction', headerName: 'Previous Reduction', type: 'numeric' },
  { field: 'do_not_mail', headerName: 'Do Not Mail', type: 'numeric' },
  // letter type
  { field: 'LetterType', headerName: 'Letter Type', type: 'string' },
  { field: 'URL', headerName: 'URL', type: 'string' },
  { field: 'NassauPID', headerName: 'Nassau PID', type: 'string' },
  { field: 'Sqft_pred', headerName: 'Est Sqft', type: 'numeric' },
];


// Hoist constant sets to module scope to avoid recreation
const ARRAY_KEYS = new Set([
  'ALFlag', 'SaleIssue', 'BadIFMV', 'OutsideDateRange', 'notMLS',
  'returnWindow', 'Bath', 'HalfBath', 'fullBaths', 'halfBaths', 'TotalGarageCap',
  'Distance', 'adj_price', 'target_id', 'subject', 'index_position',
  'value_delta', 'price_delta', 'parcel_id', 'Style', 'Acres', 'Address',
  'AssessmentVillage', 'BARDeterminationAction', 'BARDeterminationValue', 'BARFiled',
  'Bedrooms', 'CaseNotes', 'CentralAir', 'Client', 'ClientName', 'Comps',
  'ConditionIn', 'ContractDate', 'Deck', 'EQ', 'FinishedBasement', 'Fireplaces',
  'Grade', 'Heating', 'IFMV', 'IFMV_Village', 'InGroundPool', 'Kitchens',
  'Latitude', 'Longitude', 'MLSNumber', 'SalesWebScreenshot', 'ManualReview', 'MiscAdjustments',
  'MuniCode', 'MuniCode5', 'NassauPID', 'NeighborhoodCode', 'OfferValue',
  'OwnerFirstName', 'OwnerLastName', 'PCC', 'Patio', 'PhotoSource', 'PhotoURL',
  'Porch', 'PropertyAssessment', 'RAR', 'RegridBldgFtPrnt', 'RegridConvertedSqFt',
  'Remarks', 'RepID', 'ReviewedBy', 'RunDate', 'SCARDeterminationAction',
  'SCARDeterminationValue', 'SCARFiled', 'SCARIndexNumber', 'SCARSettleDate', 'VillageSCARFiled',
  'SDCode', 'SDName', 'SaleDate', 'SalePrice', 'SalePrice_pred', 'Sqft',
  'Sqft_pred', 'StreetName', 'StreetSuffix', 'SubjectMarketValue', 'Taxes',
  'TimeAdjSalePrice', 'TotalGarageCapacity', 'TotalGarageCap', 'TotalRooms', 'Town', 'TransactionID', 'Waterfront',
  'Waterview', 'WorkupValueAvg', 'WorkupValueMid', 'YearBuilt', 'ZipCode',
  'adj_price_IFMV', 'distance', 'do_not_contact', 'do_not_mail'
]);

// Unified function to process a comp row and insert it into response arrays
// partially optimized, better than before but still has some dumb logic. Core to the app and the things fixed should be the more important ones.
export const processCompRow = (rowData, responseArr, negotiationObj, subjectValues = null, insertIndex = null) => {
  // Process comp row - minimal logging for debugging
  
  if (!rowData) return;
  
  const processedData = { ...rowData };
  processedData.TransactionID = processedData.TransactionID ?? processedData.trans_id;

  const currentLength = responseArr.parcel_id?.length || 0;
  processedData.id = currentLength;

  // Initialize required arrays with default values
  const initializeArray = (fieldName, defaultValue) => {
    if (!Array.isArray(responseArr[fieldName])) {
      // Always initialize with the current length to preserve existing data
      responseArr[fieldName] = new Array(currentLength).fill(defaultValue);
    }
  };

  // Initialize all required arrays
  initializeArray('parcel_id', []);
  initializeArray('ALFlag', null); // Changed from 1 to null to preserve null values
  initializeArray('SaleIssue', null);
  initializeArray('BadIFMV', false);
  initializeArray('OutsideDateRange', 0);
  initializeArray('notMLS', 0);
  initializeArray('returnWindow', 0);
  initializeArray('Bath', '');
  initializeArray('HalfBath', '');
  initializeArray('TotalGarageCap', '');

  // Initialize price_adjustments if it doesn't exist
  if (!responseArr.price_adjustments) {
    responseArr.price_adjustments = { parcel_id: [] };
  }

  // Helper function to insert value at specific index or push to end
  const insertValue = (arr, value, index) => {
    if (index !== null && index !== undefined) {
      arr.splice(index, 0, value);
    } else {
      arr.push(value);
    }
  };

  // Helper function to get value for a key, handling both parcel_id and ParcelID formats
  const valueForKey = (k) => {
    if (k === 'parcel_id') return processedData.parcel_id ?? processedData.ParcelID ?? '';
    return processedData[k] ?? '';
  };

  // Optimized: Direct index assignment
  const setArrayValueDirect = (key, value, insertIndex) => {
    if (!Array.isArray(responseArr[key])) {
      // Always initialize with the current length to preserve existing data
      responseArr[key] = new Array(currentLength).fill('');
    }
    
    if (insertIndex !== null && insertIndex !== undefined) {
      // Check if array length is already equal to currentLength + 1 (expected length after insertion)
      if (responseArr[key].length === currentLength + 1) {
        // Prefer TransactionID match so a new sale for an existing parcel does not overwrite the old row
        const txnId = processedData.TransactionID;
        let targetIndex = -1;
        if (txnId != null && txnId !== '' && Array.isArray(responseArr.TransactionID)) {
          const needle = String(txnId);
          targetIndex = responseArr.TransactionID.findIndex(
            (id) => id != null && id !== '' && String(id) === needle,
          );
        }
        if (targetIndex === -1) {
          // Fall back to the insert slot (not parcel indexOf — that hits the first duplicate parcel)
          targetIndex = insertIndex;
        }
        if (targetIndex >= 0 && targetIndex < responseArr[key].length) {
          responseArr[key][targetIndex] = value;
        } else {
          insertValue(responseArr[key], value, insertIndex);
        }
      } else {
        // Array length is different, do insert
        insertValue(responseArr[key], value, insertIndex);
      }
    } else {
      // For append operations, use current length
      responseArr[key][currentLength] = value;
    }
  };


  // Field processing configuration
  const fieldProcessors = {
    // Set your itemized adjustments
    price_adjustments: () => {
      const shouldDebug = (processedData.parcel_id || processedData.ParcelID) === 'W2000-049-010-00001-000-0006';
      
      // Price adjustments processor - minimal logging for debugging
      const compIdForLog = processedData.parcel_id ?? processedData.ParcelID ?? '';
      const pidLenBefore = responseArr.parcel_id?.length ?? 0;

      // Keep index-based arrays aligned when inserting a new comp.
      // parcel_id is inserted earlier in this function, so lengths can diverge here.
      if (insertIndex !== null && insertIndex !== undefined) {
        if (!responseArr.adj_price) {
          responseArr.adj_price = new Array(pidLenBefore).fill(0);
        } else if (responseArr.adj_price.length === pidLenBefore - 1) {
          responseArr.adj_price.splice(insertIndex, 0, 0);
        }

        if (responseArr.price_adjustments?.total) {
          if (responseArr.price_adjustments.total.length === pidLenBefore - 1) {
            responseArr.price_adjustments.total.splice(insertIndex, 0, 0);
          }
        }
      }
      
      // Add parcel_id to price_adjustments
      insertValue(
        responseArr.price_adjustments.parcel_id,
        compIdForLog,
        insertIndex
      );
      
      // Extract subject values from response arrays if not provided
      let effectiveSubjectValues = subjectValues;
      if (!effectiveSubjectValues && responseArr.parcel_id && responseArr.parcel_id.length > 0) {
        // Get subject values from the first entry (index 0) in the response arrays
        effectiveSubjectValues = {};
        const subjectIndex = 0;
        
        // Extract all the subject property values we need for adjustments
        const subjectFields = ['IFMV', 'Sqft', 'ConditionIn', 'Grade', 'Bath', 'HalfBath', 'TotalGarageCap', 'Acres'];
        subjectFields.forEach(field => {
          if (responseArr[field] && responseArr[field][subjectIndex] !== undefined) {
            effectiveSubjectValues[field] = responseArr[field][subjectIndex];
          }
        });
        
        if (shouldDebug) {
  
        }
      }
      
      // Calculate and store price adjustments for this comp if we have subject values
      // Only run adjustment calculations if the adjusted price is blank
      if (effectiveSubjectValues && responseArr.parcel_id && responseArr.parcel_id.length > 0) {
        const compId = compIdForLog;
        
        // Check if this comp exists in the pidToIndex map — prefer TransactionID when present
        // so a new sale for an existing parcel does not reuse the old row's adjustments
        const pidToIndex = buildPidToIndexMap();
        let parcelIndex;
        const txnId = processedData.TransactionID;
        if (txnId != null && txnId !== '' && Array.isArray(responseArr.TransactionID)) {
          const needle = String(txnId);
          const txnIdx = responseArr.TransactionID.findIndex(
            (id) => id != null && id !== '' && String(id) === needle,
          );
          if (txnIdx >= 0) parcelIndex = txnIdx;
        }
        if (parcelIndex === undefined) {
          parcelIndex = pidToIndex.get(compId);
        }
        
        // If not found, try to find it with different variations
        if (parcelIndex === undefined) {
          // Try trimmed version
          const trimmedCompId = compId?.trim();
          if (trimmedCompId && trimmedCompId !== compId) {
            const trimmedIndex = pidToIndex.get(trimmedCompId);
            if (trimmedIndex !== undefined) {
              parcelIndex = trimmedIndex;
            }
          }
          
          // Try to find by partial match (in case of whitespace issues)
          if (parcelIndex === undefined) {
            for (const [pid, index] of pidToIndex.entries()) {
              if (pid && pid.trim() === compId?.trim()) {
                parcelIndex = index;
                break;
              }
            }
          }
        }
        
        if (parcelIndex !== undefined) {
          // Check if adjusted price is already calculated
          const existingAdjustedPrice = responseArr.adj_price?.[parcelIndex];
          const isExisting =
            existingAdjustedPrice !== undefined &&
            existingAdjustedPrice !== null &&
            existingAdjustedPrice !== 0 &&
            existingAdjustedPrice !== '';
          if (isExisting) {
            return;
          }
          
          // logger.log(`🔄 Running adjustment calculations for entry with blank adjusted price: ${compId}`);
          
          // Use the existing adjustment calculator for consistency
          // Use the adjustment calculator to get the adjusted price
          const adjustedPrice = calculateCompAdjustedPrice({
            subject: effectiveSubjectValues,
            comp: responseArr,
            salePrice: parseFloat(responseArr.SalePrice[parcelIndex]) || 0,
            excludeMiscAdjustments: true, // Exclude misc adjustments as requested
            memoizedCompObject: responseArr,
            parcelIndex: parcelIndex,
            logType: 'streamProcessor'
          });

          // Calculate the total adjustments by subtracting sale price from adjusted price
          const salePrice = parseFloat(responseArr.SalePrice[parcelIndex]) || 0;
          const totalAdjustments = adjustedPrice - salePrice;

          // Store the total adjustments in price_adjustments for consistency
          if (totalAdjustments !== 0) {
            if (!responseArr.price_adjustments) {
              responseArr.price_adjustments = {};
            }
            if (!responseArr.price_adjustments.total) {
              responseArr.price_adjustments.total = new Array(responseArr.parcel_id.length).fill(0);
            }
            responseArr.price_adjustments.total[parcelIndex] = totalAdjustments;
          }

          // Set adj_price using the calculated adjusted price
          if (!responseArr.adj_price) {
            responseArr.adj_price = new Array(responseArr.parcel_id.length).fill(0);
          }

          // Ensure adj_price stays index-aligned with parcel_id.
          // Using splice here can double-insert because parcel_id was already inserted earlier.
          setArrayValueDirect('adj_price', adjustedPrice, insertIndex);
          
          // Only log when we actually calculate adjusted prices
          
          // Log detailed breakdown of adjustments
          
          // Force a re-render by updating the store with the modified responseArr
          try {
            // Update the store with the modified responseArr that contains the new adjustments
            // usePersistedDataStore.getState().setCompSheet([responseArr]);
            // logger.log('🔄 Store updated with new adjustment calculations');
          } catch (e) {
            logger.log('Could not force store update for re-render:', e?.message || e);
          }
        }
      }
    },
    
    parcel_id: () => {
      const parcelIdValue = processedData.parcel_id ?? processedData.ParcelID ?? '';
      setArrayValueDirect('parcel_id', parcelIdValue, insertIndex);
    },

    TransactionID: () => {
      const n = Number(processedData.TransactionID);
      setArrayValueDirect('TransactionID', Number.isFinite(n) ? Math.trunc(n) : null, insertIndex);
    },
    
    Style: () => {
      const styleValue = processedData.BuildingStyle ?? '';
      setArrayValueDirect('Style', styleValue === '' ? 'Unknown' : styleValue, insertIndex);
    },
    
    Bath: () => {
      const bathValue = processedData.Bath ?? processedData.BathsFull ?? '';
      setArrayValueDirect('Bath', bathValue, insertIndex);
    },
    
    HalfBath: () => {
      const halfBathValue = processedData.HalfBath ?? processedData.BathsHalf ?? '';
      setArrayValueDirect('HalfBath', halfBathValue, insertIndex);
    },
    
    TotalGarageCap: () => {
      const garageValue = processedData.TotalGarageCapacity ?? processedData.GarageCapacity ?? '';
      setArrayValueDirect('TotalGarageCap', garageValue, insertIndex);
    },
    InGroundPool: () => {
      const poolValue = processedData.InGroundPool ?? processedData.Pool ?? '';
      setArrayValueDirect('InGroundPool', poolValue, insertIndex);
    },
    SalesWebScreenshot: () => {
      setArrayValueDirect('SalesWebScreenshot', processedData.SalesWebScreenshot ?? '', insertIndex);
    }
  };

  // Build PID-to-index map AFTER all data processing for accurate lookups
  const buildPidToIndexMap = () => {
    const map = new Map();
    if (responseArr.parcel_id && Array.isArray(responseArr.parcel_id)) {
      responseArr.parcel_id.forEach((pid, index) => {
        if (pid) map.set(pid, index);
      });
    }
    return map;
  };

  // Process price_adjustments AFTER all fields are processed (so parcel_id exists in arrays)

  // Process only fields that exist in the new row data
  // This ensures we only insert ONE entry per field
  const fieldsToProcess = new Set();
  
  // Add fields that have processors
  Object.keys(fieldProcessors).forEach(key => fieldsToProcess.add(key));
  
  // Add fields that exist in the processedData
  Object.keys(processedData).forEach(key => {
    if (ARRAY_KEYS.has(key)) {
      fieldsToProcess.add(key);
    }
  });
  
  // Add mapped fields from fieldMappings
  Object.entries(fieldMappings).forEach(([dbField, uiField]) => {
    if (processedData[dbField] !== undefined && ARRAY_KEYS.has(uiField)) {
      fieldsToProcess.add(uiField);
    }
  });

  // Always process ConditionIn and Grade even if missing from incoming data
  // This ensures missing comps have the same default behavior as normal algorithm responses
  if (ARRAY_KEYS.has('ConditionIn')) {
    fieldsToProcess.add('ConditionIn');
  }
  if (ARRAY_KEYS.has('Grade')) {
    fieldsToProcess.add('Grade');
  }

  // Process each field that should have data
  fieldsToProcess.forEach(key => {
    // Use field processor if available (initializes array itself via setArrayValueDirect)
    if (fieldProcessors[key]) {
      fieldProcessors[key]();
      return;
    }

    // Only operate on array-of-comps fields - don't mutate non-array fields
    if (!Array.isArray(responseArr[key])) {
      return; // Skip if not an array
    }

    const dbField = Object.entries(fieldMappings)
      .find(([db, ui]) => ui === key)?.[0] 
      || key;

    if (excludedFields.includes(dbField)) {
      return;
    }

    // Skip ALFlag here - it's handled separately in error flags section to preserve null/0 values
    if (key === 'ALFlag') {
      return;
    }

    let insertValueResult;
    if (specialFormatters[key]) {
      if (key === 'View') {
        insertValueResult = specialFormatters.View(
          processedData.Waterfront, 
          processedData.Waterview
        );
      } else if (key === 'totalBaths') {
        insertValueResult = specialFormatters.totalBaths(
          processedData.Bath, 
          processedData.HalfBath
        );
      } else {
        insertValueResult = specialFormatters[key](processedData[dbField]);
      }
    } else {
      insertValueResult = valueForKey(dbField);
    }

    // Remap condition and grade values
    // For missing values, set to 'Unknown' (frontend displays this as "Average*")
    // Only apply mapping if there's an actual value to map
    if (key === 'ConditionIn') {
      if (!insertValueResult || insertValueResult === '') {
        insertValueResult = 'Unknown';
      } else {
        insertValueResult = conditionMap[insertValueResult] || insertValueResult;
      }
    }

    if (key === 'Grade') {
      if (!insertValueResult || insertValueResult === '') {
        insertValueResult = 'Unknown';
      } else {
        insertValueResult = gradeMap[insertValueResult] || insertValueResult;
      }
    }

    setArrayValueDirect(key, insertValueResult, insertIndex);
  });

  // Map AssessmentTown to PropertyAssessment for new comps
  if (processedData.AssessmentTown !== undefined) {
    if (!responseArr.PropertyAssessment) {
      responseArr.PropertyAssessment = new Array(responseArr.parcel_id.length).fill('');
    }
    const value = processedData.AssessmentTown;
    if (insertIndex !== null) {
      // Check if array length is already equal to currentLength + 1 (expected length after insertion)
      if (responseArr.PropertyAssessment.length === currentLength + 1) {
        // Do in-place update at the specific parcel ID's index
        const parcelId = processedData.parcel_id ?? processedData.ParcelID ?? '';
        const parcelIndex = responseArr.parcel_id?.indexOf(parcelId);
        
        if (parcelIndex !== -1) {
          responseArr.PropertyAssessment[parcelIndex] = value;
        } else {
          // Parcel ID not found, do insert
          responseArr.PropertyAssessment.splice(insertIndex, 0, value);
        }
      } else {
        // Array length is different, do insert
        responseArr.PropertyAssessment.splice(insertIndex, 0, value);
      }
    } else {
      responseArr.PropertyAssessment[currentLength] = value;
    }
  }
  
  // Process price_adjustments AFTER all fields are processed (so parcel_id exists in arrays)
  if (fieldProcessors.price_adjustments) {
    fieldProcessors.price_adjustments();
  }

  // Calculate distance if we have coordinates
  const hasCoordinates = (processedData.Latitude ?? processedData.latitude) && 
                        (processedData.Longitude ?? processedData.longitude) &&
                        responseArr.Latitude?.[0] && responseArr.Longitude?.[0];
  
  if (hasCoordinates) {
    const lat = processedData.Latitude ?? processedData.latitude;
    const lon = processedData.Longitude ?? processedData.longitude;
    const distance = calculateDistance(lat, lon, responseArr.Latitude[0], responseArr.Longitude[0]);
    
    if (insertIndex !== null) {
      // Check if array length is already equal to currentLength + 1 (expected length after insertion)
      if (responseArr.Distance.length === currentLength + 1) {
        // Do in-place update at the specific parcel ID's index
        const parcelId = processedData.parcel_id ?? processedData.ParcelID ?? '';
        const parcelIndex = responseArr.parcel_id?.indexOf(parcelId);
        
        if (parcelIndex !== -1) {
          responseArr.Distance[parcelIndex] = distance;
        } else {
          // Parcel ID not found, do insert
          responseArr.Distance.splice(insertIndex, 0, distance);
        }
      } else {
        // Array length is different, do insert
        responseArr.Distance.splice(insertIndex, 0, distance);
      }
    } else {
      responseArr.Distance[currentLength] = distance;
    }
  }

  // Inventory returns arms-length as Y/N. The sheet stores 1 / 0.
  if (processedData.ALFlag === 'N' || processedData.ALFlag === 'n') {
    processedData.ALFlag = 0;
    rowData.ALFlag = 0;
  } else if (processedData.ALFlag === 'Y' || processedData.ALFlag === 'y') {
    processedData.ALFlag = 1;
    rowData.ALFlag = 1;
  }

  // Set error flags using centralized function
  const errorFlags = generateErrorFlags(processedData, negotiationObj);
  
  const setErrorFlag = (field, value) => {
    const targetParcelId = processedData.parcel_id ?? processedData.ParcelID ?? '';
    
    if (insertIndex !== null) {
      // Check if array length is already equal to currentLength + 1 (expected length after insertion)
      if (responseArr[field].length === currentLength + 1) {
        // Do in-place update at the specific parcel ID's index
        const parcelIndex = responseArr.parcel_id?.indexOf(targetParcelId);
        
        if (parcelIndex !== -1) {
          responseArr[field][parcelIndex] = value;
        } else {
          // Parcel ID not found, do insert
          responseArr[field].splice(insertIndex, 0, value);
        }
      } else {
        // Array length is different, do insert
        responseArr[field].splice(insertIndex, 0, value);
      }
    } else {
      responseArr[field][currentLength] = value;
    }
  };

  // For ALFlag, ALWAYS use the original value from processedData if it exists (including 0 and null)
  // Only fall back to errorFlags if the value is truly undefined
  // This preserves the actual value from rowData instead of defaulting to 1
  const alFlagValue = processedData.ALFlag !== undefined ? processedData.ALFlag : errorFlags.ALFlag;
  
  setErrorFlag('ALFlag', alFlagValue);
  setErrorFlag('BadIFMV', errorFlags.BadIFMV);
  setErrorFlag('OutsideDateRange', errorFlags.OutsideDateRange);
  setErrorFlag('notMLS', errorFlags.notMLS);
  setErrorFlag('returnWindow', errorFlags.returnWindow);

  // since you are now doing error checking inn row construction i dont know if you need this anymore:
  // keeping to be safe tho.
  // Generate error messages for the processed data
  const errorCodes = generateErrorCodes(errorFlags);
  const errorMessages = generateErrorMessages(errorCodes);
  
  // Set error messages in the response arrays (always set, even if empty)
  if (!responseArr.errorMessages) {
    responseArr.errorMessages = new Array(responseArr.parcel_id.length).fill([]);
  }
  
  if (insertIndex !== null) {
    responseArr.errorMessages.splice(insertIndex, 0, errorMessages);
  } else {
    responseArr.errorMessages[currentLength] = errorMessages;
  }


};


// I BELIEVE THIS IS FULLY DEPRECATED
export function useStreamProcessorNew(setFetchIterationCallback) {
  const setCompSheet = usePersistedDataStore((state) => state.setCompSheet);
  const setOptimizedComps = usePersistedDataStore((state) => state.setOptimizedComps);
  const setEvalDate = usePersistedDataStore((state) => state.setEvalDate);
  const setRar = usePersistedDataStore((state) => state.setRar);
  const setEq = usePersistedDataStore((state) => state.setEq);
  const setIsFetching = usePersistedDataStore((state) => state.setIsFetching);
  const setMarketingResponse = usePersistedDataStore((state) => state.setMarketingResponse);
  const setTotalCases = usePersistedDataStore((state) => state.setTotalCases);
  const setProcessedCount = useLocalDataStore((state) => state.setProcessedCount);
  const getCompSheet = usePersistedDataStore((state) => state.compSheet);
  const getOptimizedComps = usePersistedDataStore((state) => state.optimizedComps);
  const [loading, setLoading] = useState(false);
  

  
  // Debounced store updates to reduce render thrashing
  const debouncedStoreUpdate = useRef(
    debounce((updates) => {
      const { compSheet, optimizedComps, evalDate } = updates;
      if (compSheet) setCompSheet(compSheet);
      if (optimizedComps) setOptimizedComps(optimizedComps);
      if (evalDate) setEvalDate(evalDate);
    }, 200)
  ).current;
  
  // Use the new function-driven comp lookup hook with batch support
  const compLookups = useCompLookups({ maxConcurrent: 15, batchThreshold: 15, maxWaitTime: 1000 });
  

  const iterationRef = useRef(0);
  const marketingResponseRef = useRef([]);
  const casesWithMissingCompsRef = useRef(0);

  const processFetchResponse = useCallback(async ({ response, compStreamObjectRef, negotiationObj, currentIndex, ManualReview, abortController }) => {

    const evalDate = response[3];
    const inventoryUpdate = negotiationObj.inventoryUpdateRun ? true : false; 
    const lookupArray = inventoryUpdate ? negotiationObj.originalPidOrder : negotiationObj.uids;
    // Normalize the subject PID once per case, same as comps
    const parcelId = (response[0].parcel_id?.[0]) ?? (response[0].ParcelID?.[0]) ?? '';
    const indexInLookupArray = lookupArray.findIndex(item => item === parcelId);
    
    // Don't let lookups overwrite the slot if PID not found
    if (indexInLookupArray < 0) {
      logger.warn('PID not found in lookupArray', { parcelId, lookupArraySample: lookupArray.slice(0,5) });
      return; // or fall back to currentIndex-1, etc.
    }
    
    const updatedArray = compStreamObjectRef?.updated || new Array(lookupArray.length).fill(false);
    
    const updatedRegularComps = getCompSheet ? cloneDeep(getCompSheet) : [];
    const updatedOptimizedComps = getOptimizedComps ? cloneDeep(getOptimizedComps) : [];

    if (currentIndex === 1) {
      compStreamObjectRef.properties = [
        new Array(lookupArray.length).fill(null),
        new Array(lookupArray.length).fill(null)
      ];
      compStreamObjectRef.updated = new Array(lookupArray.length).fill(false);
      casesWithMissingCompsRef.current = 0;
      
      // Don't initialize arrays here - let the stream processing populate them naturally
      logger.log(`Stream processing initialized for ${lookupArray.length} cases`);
    }

    const currentCase = negotiationObj.cases.find(caseObj => caseObj.parcel_id === parcelId);
    const savedComps = currentCase?.Comps || {};
    const savedCompIds = Object.values(savedComps);
    const existingCompIds = response[0].parcel_id;
    
    // Create a Set of existing comp IDs for faster lookup
    const existingCompIdsSet = new Set(existingCompIds);
    
    // // Filter saved comps that don't exist in the response
    // const missingComps = savedCompIds.filter(pid => !existingCompIdsSet.has(pid));
        
    // if (missingComps.length > 0) {
    //   casesWithMissingCompsRef.current++;
    //   // logger.log('=== Starting Missing Comps Lookup ===');
    //   // logger.log('Number of missing comps:', missingComps.length);
      
    //   // Queue all missing comp lookups to be processed in background
    //   missingComps.forEach((pid) => {
    //     compLookups.enqueueLookup({
    //       pid,
    //       taxYear: negotiationObj.TaxYear,
    //       target: {
    //         ref: compStreamObjectRef,
    //         caseIndex: indexInLookupArray,
    //         negotiationObj,
    //         panes: [0, 1] // Both regular and optimized panes
    //       }
    //     });
    //   });
      
    // }

    // Normal processing - store the response data
    compStreamObjectRef.properties[0][indexInLookupArray] = parseJsonArrayFields(response[0]);
    compStreamObjectRef.properties[1][indexInLookupArray] = parseJsonArrayFields(response[1]);
    
    compStreamObjectRef.updated[indexInLookupArray] = true;
    updatedArray[indexInLookupArray] = true;

    if (currentIndex === 1 && !inventoryUpdate) {
      // if (currentIndex === 1) { 
      updatedRegularComps[indexInLookupArray] = response[0];
      updatedOptimizedComps[indexInLookupArray] = response[1];
      
      // Use debounced updates to reduce render thrashing
      debouncedStoreUpdate({
        compSheet: updatedRegularComps,
        optimizedComps: updatedOptimizedComps,
        evalDate: evalDate
      });
    }

    iterationRef.current = currentIndex;

    if(!inventoryUpdate){
      setProcessedCount(indexInLookupArray + 1);
    }

    if (setFetchIterationCallback) {
      setFetchIterationCallback(indexInLookupArray)
    }
  }, [setCompSheet, loading, setOptimizedComps, setFetchIterationCallback, getCompSheet, getOptimizedComps]);

const updateTimerRef = useRef(null);

const processMarketingResponse = useCallback(
  ({ response, compStreamObjectRef, negotiationObj, currentIndex, ManualReview }) => {
    const marketingResponse = response[0];
    const totalCases = response[3];

    const createRowFromResponse = (response, index) => {
      return fields.reduce(
        (row, { field }) => ({
          ...row,
          id: index + 1,
          // CHANGED THIS so it doesnt have ?? "Loading..." - idk if we needed that?
          [field]: response && response[field] !== null && response[field] !== undefined ? response[field] : '',
        }),
        {}
      );
    };

    if (currentIndex === 1) {
      if (marketingResponseRef.current) {
        marketingResponseRef.current = null;
      }
      marketingResponseRef.current = new Array(totalCases);
      setTotalCases(totalCases);
    }

    if (marketingResponse) {
      marketingResponseRef.current[currentIndex - 1] = createRowFromResponse(marketingResponse, currentIndex - 1);
    }

    if (updateTimerRef.current) {
      clearTimeout(updateTimerRef.current);
    }

    updateTimerRef.current = setTimeout(() => {
      if (marketingResponseRef.current) {
        setMarketingResponse([...marketingResponseRef.current.filter(Boolean)]);
      }
      updateTimerRef.current = null;
    }, 250);

    iterationRef.current = currentIndex;
    if (setFetchIterationCallback) {
      setFetchIterationCallback(currentIndex);
    }
  },
  [setFetchIterationCallback, setTotalCases, setMarketingResponse]
);

const processStream = useCallback(async ({ compStreamObjectRef, stream, negotiationObj, resetIteration=false }) => {
  if (marketingResponseRef.current) {
    marketingResponseRef.current = null;
  }
  if (updateTimerRef.current) {
    clearTimeout(updateTimerRef.current);
    updateTimerRef.current = null;
  }
  const abortController = new AbortController();
  setLoading(true);

  let reader;
  let lastDataReceivedTime = Date.now();
  let timeoutId = null;
  const STREAM_TIMEOUT_MS = 7000; // 7 seconds timeout
  let timeoutError = null;

  try {
    reader = stream.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = '';
    let iterationCount = resetIteration ? 0 : iterationRef.current;

    const cleanup = () => {
      if (reader) reader.releaseLock();
      if (timeoutId) clearTimeout(timeoutId);
      abortController.abort();
      setLoading(false);
      // setIsFetching(false);
      // logger.log('Total cases with missing comps:', casesWithMissingCompsRef.current);
    };

    // Set up timeout mechanism
    const resetTimeout = () => {
      if (timeoutId) clearTimeout(timeoutId);
      lastDataReceivedTime = Date.now();
      timeoutId = setTimeout(() => {
        logger.warn('Stream timeout: No data received for 7 seconds');
        timeoutError = new StreamFailureError('Stream timeout: No data received for 7 seconds');
        cleanup();
      }, STREAM_TIMEOUT_MS);
    };

    // Start the initial timeout
    resetTimeout();

    while (!abortController.signal.aborted) {
      // Check for timeout error
      if (timeoutError) {
        throw timeoutError;
      }
      
      try {
        const { done, value } = await reader.read();
        if (done) {
          if (buffer.trim()) {
            try {
              const update = JSON.parse(buffer);

              // Check for server error responses in final buffer
              if (isServerErrorResponse(update)) {
                cleanup();
                throw new StreamFailureError(
                  `Server error detected in final buffer: ${update.error || 'Unknown error'}`
                );
              }

              // same guard here for the final buffered chunk
              // Check for stream failure in the final buffer as well
              if (isSecondIndexEmptyObject(update)) {
                cleanup();
                throw new StreamFailureError(
                  'Detected empty object at index 1 in final buffer — upstream started sending blank chunks'
                );
              }

              await processUpdateSafely({
                update,
                negotiationObj,
                iterationCount: ++iterationCount,
                compStreamObjectRef,
                processMarketingResponse,
                processFetchResponse,
                abortController
              });
            } catch (err) {
              logger.error("Error processing final buffer:", err);
              // Rethrow StreamFailureError so it can bubble up to retry logic
              if (err instanceof StreamFailureError) {
                throw err;
              }
            }
          }
          cleanup();
          break;
        }

        // Reset timeout on data received
        resetTimeout();
        buffer += decoder.decode(value, { stream: true });

        while (buffer.includes('\n')) {
          const newlineIndex = buffer.indexOf('\n');
          const chunk = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);

          if (chunk.trim()) {
            try {
              const update = JSON.parse(chunk);

              // Check for server error responses first
              if (isServerErrorResponse(update)) {
                cleanup();
                throw new StreamFailureError(
                  `Server error detected in stream: ${update.error || 'Unknown error'}`
                );
              }

              // FAIL FAST if second index is an empty object → treat as stream failure
              // This indicates the upstream started sending blank chunks and we should fail the entire stream
              if (isSecondIndexEmptyObject(update)) {
                // ensure we close the reader / abort the controller before throwing
                cleanup();
                throw new StreamFailureError(
                  'Detected empty object at index 1 — upstream started sending blank chunks'
                );
              }

              if (!isValidUpdate(update)) {
                logger.error("Invalid update format:", update);
                continue;
              }

              iterationCount += 1;
              await processUpdateSafely({
                update,
                negotiationObj,
                iterationCount,
                compStreamObjectRef,
                processMarketingResponse,
                processFetchResponse,
                abortController
              });
            } catch (err) {
              logger.error("Error processing chunk:", err);
              // NOTE: we rethrow only our fatal StreamFailureError; others just log & continue
              if (err instanceof StreamFailureError) throw err;
            }
          }
        }
      } catch (error) {
        cleanup();
        throw error;
      }
    }
  } catch (error) {
    logger.error("Stream processing error:", error);
    
    // Rethrow StreamFailureError so it can be handled by the caller's retry logic
    if (error instanceof StreamFailureError) {
      throw error;
    }
    
    // Check for timeout error that might have been set
    if (timeoutError) {
      throw timeoutError;
    }
    
    // For other errors, show generic toast
    toast.error("Error processing stream data");
  } finally {
    if (reader) reader.releaseLock();
    setLoading(false);
    setIsFetching(false);
    logger.log('Total cases with missing comps:', casesWithMissingCompsRef.current);
    
    // Flush the comp lookup queue without blocking the main thread
    try {
      // Force batch processing of any remaining items
      compLookups.forceBatchProcess();
      
      compLookups.flush().catch(error => {
        logger.error('Error flushing comp lookups:', error);
      });
      logger.log('Comp lookups flush initiated');
    } catch (error) {
      logger.error('Error initiating comp lookups flush:', error);
    }
  }
}, [processFetchResponse]);

const isValidUpdate = (update) => {
  return Array.isArray(update) && 
         update.length >= 4 && 
         update[0] !== null && 
         update[1] !== null;
};

const processUpdateSafely = async ({
  update,
  negotiationObj,
  iterationCount,
  compStreamObjectRef,
  processMarketingResponse,
  processFetchResponse,
  abortController
}) => {
  try {
    if (negotiationObj.reportType === 'marketing') {
      await processMarketingResponse({
        response: update,
        compStreamObjectRef,
        negotiationObj,
        currentIndex: iterationCount,
      });
    } else {
      await processFetchResponse({
        response: update,
        compStreamObjectRef,
        negotiationObj,
        currentIndex: iterationCount,
        abortController,
      });
    }
  } catch (error) {
    logger.error("Error in update processing:", error);
    // Rethrow StreamFailureError so it can bubble up to the retry logic
    if (error instanceof StreamFailureError) {
      throw error;
    }
  }
};

useEffect(() => {
  const abortController = new AbortController();
  
  return () => {
    abortController.abort();
    setLoading(false);
    // setIsFetching(false);
    marketingResponseRef.current = null;
    if (updateTimerRef.current) {
      clearTimeout(updateTimerRef.current);
      updateTimerRef.current = null;
    }
  };
}, []);

return {
  processStream,
  loading,
  getCompLookupStatus: () => compLookups.getStatus(),
  waitForCompLookups: async () => {
    await compLookups.flush();
    return true;
  },
  finalizeCompLookups: async () => {
    return await compLookups.flush();
  },
  getQueueStatus: () => compLookups.getStatus(),
  // Flush the comp lookup queue
  flushCompLookups: async () => {
    return await compLookups.flush();
  },
  // Get status of comp lookups
  getCompLookupStatus: () => compLookups.getStatus(),
  // New batch processing methods
  processBatchCompLookups: () => compLookups.processBatch(),
  forceBatchCompLookups: () => compLookups.forceBatchProcess(),
  getCompLookupQueueStats: () => compLookups.getQueueStats(),
  getCompLookupBatchThreshold: () => compLookups.getBatchThreshold(),

};
}